// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { type Setting, type SettingChange, SettingChangeType } from '@api/types';

/**
 * Diffs edited custom settings against the original set by setting `name`
 * (the identity within the custom-settings category). Added or value-changed
 * settings produce an UPSERT change; removed settings produce a DELETE change;
 * unchanged settings are omitted (REQ-041).
 */
export function buildCustomSettingsChanges(original: Setting[], edited: Setting[]): SettingChange[] {
  const originalByName = new Map(original.map((s) => [s.name, s]));
  const editedByName = new Map(edited.map((s) => [s.name, s]));

  const upserts: SettingChange[] = edited
    .filter((s) => originalByName.get(s.name)?.value !== s.value)
    .map((setting) => ({ changeType: SettingChangeType.UPSERT, setting }));

  const deletes: SettingChange[] = original
    .filter((s) => !editedByName.has(s.name))
    .map((setting) => ({ changeType: SettingChangeType.DELETE, setting }));

  return [...upserts, ...deletes];
}
